import { createSlice } from '@reduxjs/toolkit';

const initialState = {
	data: [],
	loading: false,
	// "Load older events" appends the next page of the SAME query; `querySeq` tells a stale page apart.
	loadingMore: false,
	query: null,
	querySeq: 0,
	error: null,
	// Result mix across the WHOLE filtered query (server-computed), not the loaded page, so the
	// tiles agree with the total next to them.
	summary: { success: 0, denied: 0, error: 0 },
	pagination: {
		total: 0,
		page: 1,
		pageSize: 50
	},
	// Export is a separate, query-scoped fetch: the grid holds one page, but an audit export
	// that silently hands back 50 of 4,000 rows is worse than no export at all.
	exporting: false,
	exportPayload: null
};

const auditLogsSlice = createSlice({
	name: 'auditLogs',
	initialState,
	reducers: {
		loadAuditLogs: (state, action) => {
			state.loading = true;
			state.loadingMore = false;
			state.error = null;
			state.query = action.payload || {};
			state.querySeq += 1;
		},
		loadAuditLogsSuccess: (state, action) => {
			state.loading = false;
			state.data = action.payload.data || [];
			state.summary = action.payload.summary || initialState.summary;
			state.pagination = {
				...state.pagination,
				...(action.payload.pagination || {})
			};
		},
		loadMoreAuditLogs: (state) => {
			state.loadingMore = true;
		},
		// Dropped when the filters changed mid-flight, or older rows of the old query would join the new list.
		loadMoreAuditLogsSuccess: (state, action) => {
			if (action.payload.querySeq !== state.querySeq) return;
			state.loadingMore = false;
			// Safety net for OFFSET paging, where an event logged since page 1 repeats a row.
			const seen = new Set(state.data.map((row) => row.auditID));
			state.data = [...state.data, ...(action.payload.data || []).filter((row) => !seen.has(row.auditID))];
			state.pagination = { ...state.pagination, ...(action.payload.pagination || {}) };
		},
		loadMoreAuditLogsError: (state) => {
			state.loadingMore = false;
		},
		loadAuditLogsError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			// Drop the previous result set: stale rows would not answer the query on screen, and after a
			// 403 (demoted admin, org switch) they may belong to another org.
			state.data = [];
			state.summary = initialState.summary;
			state.pagination = initialState.pagination;
		},
		exportAuditLogs: (state) => {
			state.exporting = true;
			state.exportPayload = null;
		},
		exportAuditLogsSuccess: (state, action) => {
			state.exporting = false;
			state.exportPayload = action.payload;
		},
		exportAuditLogsError: (state, action) => {
			state.exporting = false;
			state.exportPayload = null;
			state.error = action.payload;
		},
		// Cleared by the page once the file is written, so a later render can't re-fire the
		// write effect from a stale payload.
		clearAuditLogExport: (state) => {
			state.exporting = false;
			state.exportPayload = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// Profile switch changes org without changing filters, so reset or the old org's events linger. Keyed
		// on *Success*: resetting on a failed switch leaves orgID unchanged, so nothing refetches (as accessWindows).
		builder.addCase('auth/switchProfileSuccess', () => initialState);
	}
});

export const auditLogActions = auditLogsSlice.actions;

export default auditLogsSlice.reducer;
